import { useMemo, useCallback } from "react";
import { Style } from "../../../ducks/style/style";
import { DEFAULT_ID_ATTRIBUTE_NAME } from "../../../evergis/constants";
import { FeatureCondition } from "../../../evergis/core/condition/FeatureCondition";
import { useRasterTempStyle } from "./useRasterTempStyle";
import { evaluateCondition } from "../../../evergis/core/condition/evaluateCondition";
import { RasterSymbol } from "../../../interfaces/style";
import { FeatureDcExtended } from "../../../ducks/selectFeatures/types";

const findRasterStyleByFeature = (style: Style, feature?: FeatureDcExtended): Style | null => {
  const { id, attributes } = feature || {};
  const { children, raster: rootRaster } = style || {};

  if (!children || !children.length) {
    return rootRaster ? style : null;
  }

  return (
    children.find(
      child =>
        child.condition &&
        ((!!attributes && evaluateCondition(child.condition, attributes)) ||
          evaluateCondition(child.condition, { [DEFAULT_ID_ATTRIBUTE_NAME]: +id })),
    ) || null
  );
};

export const useFeatureRasterStyle = (
  feature?: FeatureDcExtended,
): {
  featureStyle: Style | null;
  rootRaster: RasterSymbol | undefined;
  updateRasterStyle: (setVisibleRasters: boolean) => void;
} => {
  const { id, layer } = feature || {};
  const { style, setStyle } = useRasterTempStyle(layer);

  const featureStyle: Style | null = useMemo(() => findRasterStyleByFeature(style, feature), [style, feature]);

  const updateRasterStyle = useCallback(
    (setVisibleRasters: boolean) => {
      if (!style) return;

      const newChildren = style.children ? style.children.slice() : [];

      if (newChildren.length) {
        const specificStyle = newChildren.find(child => {
          return child.raster && child.raster.opacity === 0 && new FeatureCondition(child.condition).valid;
        });
        const styleIndex = newChildren.indexOf(specificStyle);

        if (!specificStyle || styleIndex === -1) return;

        const featureCondition = new FeatureCondition(specificStyle.condition);

        if (setVisibleRasters) {
          featureCondition.remove(id);
        } else {
          featureCondition.add(id);
        }

        if (featureCondition.valid) {
          newChildren[styleIndex] = {
            ...specificStyle,
            condition: featureCondition.toString(),
          };
        } else {
          newChildren.splice(styleIndex, 1);
        }

        setStyle({ ...style, children: newChildren.length ? newChildren : null });
      } else {
        if (setVisibleRasters) return;

        const featureCondition = new FeatureCondition(style.condition);

        featureCondition.add(id);

        if (style.raster) {
          const newStyle: Style = {
            ...style,
            children: null,
            raster: { ...style.raster, opacity: 0 },
            condition: featureCondition.toString(),
          };

          newChildren.unshift(newStyle);
          setStyle({ ...style, children: newChildren });
        }
      }
    },
    [style, setStyle, id],
  );

  return {
    featureStyle,
    rootRaster: style && style.raster,
    updateRasterStyle,
  };
};
